<template>
  <div class="profile-hero-section">
    <HeroSection
      title="Your Profile"
      subtitle="You can modify and adjust your settings here."
      backgroundColor="#003d6a"
      backgroundImage="/images/background-2.svg"
      backgroundImageMobile="/images/background-2.svg"
      illustrationImage="/images/profile-illustration-1.png"
      illustrationAlt="Profile Illustration"
    />
  </div>

  <!-- Mobile Profile Info Section -->
  <div v-if="user" class="mobile-profile-info">
    <div class="mobile-profile-image-wrapper">
      <div class="mobile-profile-image-container">
        <div v-if="imageLoading" class="mobile-image-placeholder">
          <svg
            width="30"
            height="30"
            viewBox="0 0 24 24"
            fill="#999"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            />
          </svg>
        </div>
        <img
          v-if="user && !imageLoading"
          class="mobile-profile-image"
          :src="user.image ? user.image : '/images/no-profile.webp'"
          alt="User Image"
          @load="imageLoading = false"
          @error="imageLoading = false"
        />
        <div class="mobile-camera-icon">
          <svg
            width="20"
            height="17.5"
            viewBox="0 0 20 17.5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.3333 4.58333H15.4167L13.75 2.91667H6.25L4.58333 4.58333H1.66667C0.75 4.58333 0 5.33333 0 6.25V15.4167C0 16.3333 0.75 17.0833 1.66667 17.0833H18.3333C19.25 17.0833 20 16.3333 20 15.4167V6.25C20 5.33333 19.25 4.58333 18.3333 4.58333ZM10 14.5833C7.7 14.5833 5.83333 12.7167 5.83333 10.4167C5.83333 8.11667 7.7 6.25 10 6.25C12.3 6.25 14.1667 8.11667 14.1667 10.4167C14.1667 12.7167 12.3 14.5833 10 14.5833ZM10 8.33333C8.61667 8.33333 7.5 9.45 7.5 10.8333C7.5 12.2167 8.61667 13.3333 10 13.3333C11.3833 13.3333 12.5 12.2167 12.5 10.8333C12.5 9.45 11.3833 8.33333 10 8.33333Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
    </div>
    <div class="mobile-user-info">
      <h1 class="mobile-user-name">{{ user.firstname }} {{ user.lastname }}</h1>
      <p class="mobile-user-email">{{ user.email }}</p>
      <div class="mobile-public-status">
        <img src="/images/eye.svg" alt="" />
        <span>Public</span>
      </div>
    </div>
  </div>

  <!-- Mobile Navigation Tabs -->
  <div class="mobile-navigation-tabs">
    <button
      v-for="(menu, index) in menuItems"
      :key="index"
      :class="{
        'mobile-nav-tab': true,
        active: isActiveRoute(menu.route),
      }"
      @click="navigateToRoute(menu.route)"
    >
      {{ menu.label }}
    </button>
  </div>

  <!-- Main Content Section -->
  <div class="container-profile container-xxl mb-10">
    <div class="sidebar-profile">
      <div class="profile-photo-section">
        <div class="profile-image-wrapper">
          <div class="profile-image-container">
            <div v-if="!user || imageLoading" class="image-placeholder">
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="#999"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </div>
            <img
              v-if="user && !imageLoading"
              class="profile-image"
              :src="user.image ? user.image : '/images/no-profile.webp'"
              alt="User Image"
              @load="imageLoading = false"
              @error="imageLoading = false"
            />
            <div class="camera-icon">
              <svg
                width="20"
                height="17.5"
                viewBox="0 0 20 17.5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.3333 4.58333H15.4167L13.75 2.91667H6.25L4.58333 4.58333H1.66667C0.75 4.58333 0 5.33333 0 6.25V15.4167C0 16.3333 0.75 17.0833 1.66667 17.0833H18.3333C19.25 17.0833 20 16.3333 20 15.4167V6.25C20 5.33333 19.25 4.58333 18.3333 4.58333ZM10 14.5833C7.7 14.5833 5.83333 12.7167 5.83333 10.4167C5.83333 8.11667 7.7 6.25 10 6.25C12.3 6.25 14.1667 8.11667 14.1667 10.4167C14.1667 12.7167 12.3 14.5833 10 14.5833ZM10 8.33333C8.61667 8.33333 7.5 9.45 7.5 10.8333C7.5 12.2167 8.61667 13.3333 10 13.3333C11.3833 13.3333 12.5 12.2167 12.5 10.8333C12.5 9.45 11.3833 8.33333 10 8.33333Z"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div class="navigation-buttons">
        <button
          v-for="(menu, index) in menuItems"
          :key="index"
          :class="{
            'nav-button': true,
            active: isActiveRoute(menu.route),
          }"
          @click="navigateToRoute(menu.route)"
        >
          {{ menu.label }}
        </button>
      </div>
    </div>
    <div class="main-content">
      <div v-if="user" class="content-header">
        <div class="user-info">
          <h1 class="profile_user-name">
            {{ user.firstname }} {{ user.lastname }}
          </h1>
          <p class="user-email">{{ user.email }}</p>
        </div>
        <div class="public-status">
          <img src="/images/eye.svg" alt="" />
          <span>Public</span>
        </div>
      </div>
      <!-- Render child route components -->
      <div class="content-container">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import cookiesService from "@/services/cookiesService";
import userService from "@/services/userService";
import HeroSection from "@/components/common/HeroSection.vue";

const router = useRouter();
const route = useRoute();

const user = ref(null);
const isAdmin = ref(false);
const imageLoading = ref(true);

const menuItems = ref([
  { label: "Account", route: "/profile/account" },
  { label: "Security Settings", route: "/profile/security-settings" },
  { label: "My Resources", route: "/profile/my-resources" },
]);

// Load user profile data
const getProfile = async () => {
  try {
    imageLoading.value = true;
    const authToken = await cookiesService.getCookie("authToken");
    if (authToken) {
      const response = await userService.getProfile(authToken.token);
      if (response.status === 200) {
        user.value = response.data.data;
        isAdmin.value = response.data.data.user_group_id === 1;

        cookiesService.setCookie("authToken", authToken);
      }
    }
  } catch (error) {
    console.error("Failed to load profile:", error);
    imageLoading.value = false;
  }
};

const navigateToRoute = (routePath) => {
  router.push(routePath);
};

const isActiveRoute = (routePath) => {
  return route.path === routePath;
};

// Set background image for the dynamic height div
const setBackgroundImage = () => {
  const dynamicHeightDiv = document.getElementById("dynamicHeightDiv");
  if (dynamicHeightDiv) {
    dynamicHeightDiv.style.backgroundImage = "url('/images/background-2.svg')";
    const width = window.innerWidth;
    const height = width * (200 / 1440);
    dynamicHeightDiv.style.height = `${height}px`;
    dynamicHeightDiv.style.backgroundSize = "cover";
    dynamicHeightDiv.style.borderRadius = "0px 0px 25px 25px";
  }
};

onMounted(() => {
  setBackgroundImage();
  window.addEventListener("resize", setBackgroundImage);
  getProfile();

  // Redirect to account if on base profile route
  if (route.path === "/profile") {
    router.push("/profile/account");
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", setBackgroundImage);
});
</script>

<style scoped>
.container-profile {
  display: flex;
  flex-direction: row;
  gap: 40px;
  margin: 0;
  padding: 0 32px;
  min-height: 100px;
  margin-top: 24px;
}

.profile-hero-section :deep(.illustration-image) {
  margin-top: 50px;
}

.sidebar-profile {
  width: 212px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: center;
  flex-shrink: 0;
}

.profile-photo-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.profile-image-wrapper {
  position: relative;
}

.profile-image-container {
  position: relative;
  width: 180px;
  height: 180px;
  border-radius: 90px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 8px solid var(--primary-blue, #003d6a);
}

.image-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #d0d0d0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-image {
  width: 100%;
  height: 100%;
  border-radius: 90px;
  object-fit: cover;
}

.camera-icon {
  position: absolute;
  bottom: 0;
  right: 10px;
  width: 40px;
  height: 40px;
  background-color: var(--primary-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.navigation-buttons {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
}

.content-container {
  background-color: #ffffff;
  border: 1px solid var(--primary-blue);
  border-radius: 16px;
  padding: 16px;
  width: 100%;
}

.nav-button {
  width: 100%;
  height: 56px;
  padding: 10px 16px;
  border: none;
  border-radius: 12px;
  background-color: transparent;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  letter-spacing: 0.024px;
  cursor: pointer;
  text-align: center;
  transition: all 0.3s ease;
}

.nav-button.active {
  background-color: var(--primary-blue);
  color: #ffffff;
}

.nav-button:hover:not(.active) {
  background-color: rgba(0, 61, 106, 0.1);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 40px;
  min-width: 0;
}

.content-header {
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  color: var(--primary-blue);
}

.profile_user-name {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  margin: 0;
  color: var(--primary-blue);
}

.user-email {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xl);
  line-height: 32px;
  margin: 0;
  color: var(--primary-blue);
}

.public-status {
  display: flex;
  gap: 8px;
  align-items: center;
  color: var(--green-dark-1);
}

.public-status img {
  width: 24px;
  height: 24px;
}

.public-status span {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: var(--green-dark-1);
}

.card-header-created {
  color: #ccc;
  font-size: 0.8em;
}

.card-header-date {
  color: #3b4648;
  font-size: 0.8em;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .container-profile {
    flex-direction: column;
    padding: 0 16px;
    margin-top: 0;
  }

  .sidebar-profile {
    display: none;
  }

  .content-header {
    display: none;
  }

  .mobile-profile-info {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 16px;
    background-color: #ffffff;
    position: relative;
    z-index: 10;
    margin-left: 0;
    margin-right: 0;
  }

  .mobile-profile-image-wrapper {
    position: relative;
    flex-shrink: 0;
  }

  .mobile-profile-image-container {
    position: relative;
    width: 150px;
    height: 150px;
    border-radius: 75px;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 8px solid var(--primary-blue, #003d6a);
  }

  .mobile-image-placeholder {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: #d0d0d0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-profile-image {
    width: 100%;
    height: 100%;
    border-radius: 75px;
    object-fit: cover;
  }

  .mobile-camera-icon {
    position: absolute;
    bottom: 6.67px;
    right: 0;
    width: 35px;
    height: 35px;
    background-color: var(--primary-blue);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6.667px;
  }

  .mobile-camera-icon svg {
    width: 20px;
    height: 20px;
  }

  .mobile-user-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
  }

  .mobile-user-name {
    font-family: "League Spartan", sans-serif;
    font-weight: 700;
    font-size: var(--fs-xl);
    line-height: 32px;
    margin: 0;
    color: var(--primary-blue);
  }

  .mobile-user-email {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-medium);
    line-height: 24px;
    letter-spacing: 0.024px;
    margin: 0;
    color: var(--primary-blue);
  }

  .mobile-public-status {
    display: flex;
    gap: 8px;
    align-items: center;
    color: var(--green-light-1);
  }

  .mobile-public-status img {
    width: 20px;
    height: 20px;
  }

  .mobile-public-status span {
    font-family: "League Spartan", sans-serif;
    font-weight: 400;
    font-size: var(--fs-small);
    line-height: 20px;
    letter-spacing: 0.014px;
    color: var(--green-dark-1);
  }

  .mobile-navigation-tabs {
    display: flex;
    gap: 24px;
    padding: 8px 16px;
    background-color: #f2f3fb;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .mobile-navigation-tabs::-webkit-scrollbar {
    display: none;
  }

  .mobile-nav-tab {
    flex: 1;
    min-width: fit-content;
    height: 32px;
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    color: var(--primary-blue);
    font-family: "League Spartan", sans-serif;
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.048px;
    cursor: pointer;
    text-align: center;
    transition: all 0.3s ease;
    white-space: nowrap;
  }

  .mobile-nav-tab.active {
    background-color: var(--primary-blue);
    color: #ffffff;
  }

  .content-container {
    margin-top: 16px;
  }
}

@media (min-width: 769px) {
  .mobile-profile-info,
  .mobile-navigation-tabs {
    display: none;
  }
}
</style>
